import { useEffect, useMemo, useRef, useState } from 'react'
import { useSession } from './api/session'
import { Accounts } from './console/Accounts'
import { Journal } from './console/Journal'
import { Report } from './console/Report'
import { Rules } from './console/Rules'
import { readLaunchEnv } from './env'
import { t, type Key } from './i18n/dict'
import { activeLocale } from './i18n/locale'
import { ProblemLine } from './ProblemLine'
import { createCTraderHost, createFakeHost, type GatekeeperHost } from './sdk/adapter'
import { Ticket } from './ticket/Ticket'

/**
 * The shell: sign in once, then tabs. Ticket is first, so the ergonomics gate is not lost to
 * a settings page; the console screens (Accounts, Rules, Report, Journal) are the TUI's, over
 * the same loaders, as the trader. It reads what cTrader passed at launch: theme, placement,
 * the symbol in front of the trader.
 */
type Tab = 'ticket' | 'rules' | 'report' | 'journal'

/** The label is a translation, not the identifier with a capital letter. `Report` is `Bericht`
 * in German and there is no casing rule that gets you there. */
const TABS: ReadonlyArray<readonly [Tab, Key]> = [
  ['ticket', 'tab.ticket'],
  ['rules', 'tab.rules'],
  ['report', 'tab.report'],
  ['journal', 'tab.journal'],
]

export function App() {
  const env = useMemo(() => readLaunchEnv(), [])
  const session = useSession()
  const [tab, setTab] = useState<Tab>('ticket')
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [authorisationExpiresAt, setAuthorisationExpiresAt] = useState<string | null | undefined>(undefined)
  const [host, setHost] = useState<GatekeeperHost | null>(null)
  const [hostError, setHostError] = useState<string | null>(null)
  // What cTrader tells us (a broker login) and what the guardian keys by (a ctid account
  // id) are two different numbers. The SDK carries only the first.
  const [activeLogin, setActiveLogin] = useState<number | null>(null)
  const [activeAccount, setActiveAccount] = useState<number | null>(null)
  const [bindError, setBindError] = useState<string | null>(null)

  // Connect to cTrader once; without a host there is nothing to trade with. The console
  // screens work regardless — they only need the API.
  useEffect(() => {
    const h = env.hasHost ? createCTraderHost() : import.meta.env.DEV ? createFakeHost() : null
    if (!h) {
      setHostError(t('host.notEmbedded'))
      return
    }
    let cancelled = false
    h.connect()
      .then(() => h.getAccount())
      .then((a) => {
        if (cancelled) return
        setHost(h)
        setActiveLogin(a.login)
      })
      .catch((error: unknown) => !cancelled && setHostError(t('host.silent', { error: String(error) })))
    return () => {
      cancelled = true
      h.disconnect()
    }
  }, [env.hasHost])

  // cTrader lets the trader switch account underneath us and the SDK publishes no event for
  // it — confirm, execution, quote, register, serverData, and nothing about the account. So
  // ask. Until this existed, switching account left every screen describing the account you
  // had left while the ticket would have sent its order to the one you moved to.
  useEffect(() => {
    if (!host) return undefined
    const timer = setInterval(() => {
      void host
        .getAccount()
        .then((a) => setActiveLogin((current) => (current === a.login ? current : a.login)))
        .catch(() => undefined)
    }, 5_000)
    return () => clearInterval(timer)
  }, [host])

  // The login is what cTrader shows and the SDK reports; the guardian knows accounts by their
  // Open API id. Only the guardian holds both, so the translation happens here, against the
  // accounts this trader's own consent reaches. An account it has never discovered cannot be
  // guarded either, so saying so is the honest answer.
  useEffect(() => {
    if (session.state.status !== 'signed-in' || activeLogin === null) return undefined
    let cancelled = false
    session.api
      .discovered()
      .then((list) => {
        if (cancelled) return
        const match = list.find((a) => a.traderLogin === activeLogin)
        setActiveAccount(match ? match.accountId : null)
        setBindError(match ? null : t('bind.undiscovered', { login: activeLogin }))
      })
      .catch(() => {
        if (!cancelled) setBindError(t('bind.unknown'))
      })
    return () => {
      cancelled = true
    }
  }, [session.api, session.state.status, activeLogin])

  // Guarding an account is setup, so it lives behind the cog — but a lapsed authorisation
  // stops the guardian seeing anything at all, and that must never be something you only
  // find by opening a settings sheet.
  useEffect(() => {
    if (session.state.status !== 'signed-in') return
    session.api
      .accounts()
      .then((v) => setAuthorisationExpiresAt(v.authorisationExpiresAt))
      .catch(() => undefined)
  }, [session.api, session.state.status, settingsOpen])

  useEffect(() => {
    document.documentElement.dataset['theme'] = env.theme
  }, [env.theme])

  // cTrader sends a `lang` we have no recorded list of, and guessing which locale to build
  // next is how you build the wrong one. Report an unresolved tag once, over the channel that
  // already exists, and let the third locale be chosen from evidence.
  const langReported = useRef(false)
  useEffect(() => {
    if (session.state.status !== 'signed-in' || langReported.current || activeLocale().known) return
    langReported.current = true
    session.api.telemetry('lang-unresolved', { lang: env.lang })
  }, [session.api, session.state.status, env.lang])

  const openUrl = (url: string) => {
    void (host?.openUrl(url) ?? Promise.resolve(false)).then((opened) => {
      if (!opened) window.open(url, '_blank', 'noopener')
    })
  }

  // Every screen talks about one account: the one cTrader has selected. The plugin never
  // offers a second selector — cTrader's own is the answer, and two answers to that question
  // is how a ticket comes to be evaluated against one account and sent to another.
  const account = activeAccount
  const nowMs = Date.now() // display only — never a rule decision; those use ServerClock
  const authorisationLapsed =
    authorisationExpiresAt === null || (typeof authorisationExpiresAt === 'string' && Date.parse(authorisationExpiresAt) < nowMs)

  return (
    <div className={`app ${env.narrow ? 'narrow' : ''}`}>
      {/* Who you are and how to stop being them belong with the accounts, not across the top
          of every screen: you read the name once and then it is just width. The signed-out
          case needs nothing here either — the Connect screen below already says so. */}
      <header className="bar">
        <strong>Gatekeeper</strong>
        {session.state.status === 'signed-in' && (
          <button
            type="button"
            className={authorisationLapsed ? 'cog warn' : 'cog'}
            onClick={() => setSettingsOpen(true)}
            aria-label={t('app.settings')}
            title={t('accounts.title')}
          >
            ⚙
          </button>
        )}
      </header>

      <nav className="tabs" aria-label={t('app.sections')}>
        {TABS.map(([id, label]) => (
          <button key={id} type="button" className={tab === id ? 'on' : ''} onClick={() => setTab(id)} aria-current={tab === id}>
            {t(label)}
          </button>
        ))}
      </nav>

      <main>
        {session.state.status === 'signed-in' && bindError && <p className="bad banner">{bindError}</p>}

        {session.state.status === 'signed-in' && authorisationLapsed && (
          <p className="bad banner">
            {t('auth.lapsed')}{' '}
            <button type="button" className="link" onClick={() => setSettingsOpen(true)}>
              {t('auth.openAccounts')}
            </button>
          </p>
        )}

        {session.state.status === 'checking' && <p className="muted">{t('auth.checking')}</p>}

        {session.state.status === 'signed-out' && (
          <section className="connect">
            <h1>{t('connect.title')}</h1>
            <p>{t('connect.blurb')}</p>
            {session.state.message && <ProblemLine problem={session.state.message} />}
            <button type="button" className="primary" onClick={() => void session.signIn('accounts', openUrl)}>
              {t('connect.watchOnly')}
            </button>
            <button type="button" onClick={() => void session.signIn('trading', openUrl)}>
              {t('connect.trading')}
            </button>
            <small>{t('connect.scopeNote')}</small>
          </section>
        )}

        {session.state.status === 'signing-in' && (
          <section className="connect">
            <h1>{t('connect.waitingTitle')}</h1>
            <p>{session.state.message}</p>
            <a href={session.state.url} target="_blank" rel="noopener noreferrer">
              {t('connect.reopen')}
            </a>
          </section>
        )}

        {session.state.status === 'signed-in' && tab === 'ticket' && (
          hostError ? (
            <p className="bad">{hostError}</p>
          ) : host && activeAccount ? (
            <Ticket api={session.api} host={host} account={activeAccount} initialSymbol={env.symbol} />
          ) : (
            <p className="muted">{t('host.connecting')}</p>
          )
        )}

        {session.state.status === 'signed-in' && tab !== 'ticket' && (
          account === null ? (
            <p className="muted">{t('bind.waiting')}</p>
          ) : tab === 'rules' ? (
            <Rules api={session.api} account={account} />
          ) : tab === 'report' ? (
            <Report api={session.api} account={account} />
          ) : (
            <Journal api={session.api} account={account} />
          )
        )}
      </main>

      {settingsOpen && session.state.status === 'signed-in' && (
        <div
          className="sheet-backdrop"
          role="presentation"
          onClick={(e) => e.target === e.currentTarget && setSettingsOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setSettingsOpen(false)}
        >
          <div className="sheet" role="dialog" aria-modal="true" aria-label={t('accounts.title')}>
            <header className="sheet-head">
              <strong>{t('accounts.title')}</strong>
              <button type="button" className="link" onClick={() => setSettingsOpen(false)}>
                {t('accounts.close')}
              </button>
            </header>
            <p className="whoami muted">
              {t('accounts.signedInAs')} <strong>{session.state.me.name}</strong>
              {session.state.me.ctidUserId ? ` · ctid:${session.state.me.ctidUserId}` : ''}{' '}
              <button type="button" className="link" onClick={() => void session.signOut()}>
                {t('accounts.signOut')}
              </button>
            </p>
            <Accounts api={session.api} nowMs={nowMs} activeAccount={activeAccount} />
          </div>
        </div>
      )}
    </div>
  )
}
